import React, { useState, useEffect, useCallback } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  IndexTable,
  Badge,
  Spinner,
  EmptyState,
  Box,
  Icon,
  InlineGrid,
  ButtonGroup,
  Tooltip,
  ProgressBar,
  Avatar,
  InlineStack,
  BlockStack,
  useIndexResourceState
} from '@shopify/polaris';
import {
  OrdersMajor,
  EmailMajor,
  CircleTickMajor,
  ViewMajor,
  SettingsMajor,
  CreditCardMajor
} from '@shopify/polaris-icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useShopifyNavigation } from '../hooks/useShopifyNavigation';
import api from '../utils/axiosConfig';
import ZyzfyIntegrationBanner from '../components/ZyzfyIntegrationBanner';

function Home() {
  const navigate = useNavigate();
  const navigateWithContext = useShopifyNavigation();
  const { t } = useTranslation();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalOrders: 0,
    activeJourneys: 0,
    emailsSent: 0,
    deliveredOrders: 0
  });
  const [recentOrders, setRecentOrders] = useState([]);
  const [subscription, setSubscription] = useState(null);

  // Selection state for recent orders table
  const resourceName = {
    singular: 'order',
    plural: 'orders',
  };

  const {
    selectedResources,
    allResourcesSelected,
    handleSelectionChange,
  } = useIndexResourceState(recentOrders);

  useEffect(() => {
    // Wait a bit for App Bridge to initialize and URL to be fixed
    const timer = setTimeout(() => {
      loadDashboardData();
    }, 500);
    
    return () => clearTimeout(timer);
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      
      // Load dashboard stats in parallel
      const [ordersRes, subscriptionRes, summaryRes] = await Promise.all([
        api.get('/dashboard/recent-orders?limit=5'),
        api.get('/billing/subscription'),
        api.get('/dashboard/summary')
      ]);
      
      setRecentOrders(ordersRes.data.orders || []);
      setSubscription(subscriptionRes.data.subscription);
      
      // Get real stats from dashboard API
      if (summaryRes.data.stats) {
        setStats({
          totalOrders: summaryRes.data.stats.totalOrders || 0,
          activeJourneys: summaryRes.data.stats.activeEmailJourneys || 0,
          emailsSent: 0, // This metric is not tracked in the database
          deliveredOrders: summaryRes.data.stats.ordersWithTracking || 0
        });
      }
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      pending: { status: 'warning', content: t('dashboard.orderStatus.pending') },
      paid: { status: 'success', content: t('dashboard.orderStatus.paid') },
      shipped: { status: 'info', content: t('dashboard.orderStatus.shipped') },
      delivered: { status: 'success', content: t('dashboard.orderStatus.delivered') },
      cancelled: { status: 'critical', content: t('dashboard.orderStatus.cancelled') }
    };
    
    return statusMap[status] || { status: 'new', content: status };
  };

  // Modern row renderer for IndexTable
  const rowMarkup = recentOrders.map((order, index) => (
    <IndexTable.Row
      id={order.id}
      key={order.id}
      selected={selectedResources.includes(order.id)}
      position={index}
    >
      <IndexTable.Cell>
        <InlineStack gap="300" align="start">
          <Avatar
            customer
            size="sm"
            name={order.customerName || order.customerEmail || 'Customer'}
          />
          <BlockStack gap="100">
            <Text variant="bodyMd" fontWeight="semibold" as="span">
              {order.name}
            </Text>
            <Text variant="bodySm" as="span" color="subdued">
              {order.customerEmail || 'N/A'}
            </Text>
          </BlockStack>
        </InlineStack>
      </IndexTable.Cell>
      
      <IndexTable.Cell>
        <InlineStack gap="200" align="start">
          <Icon source={CreditCardMajor} color="subdued" />
          <Text variant="bodyMd" fontWeight="semibold" as="span">
            {formatCurrency(order.totalPrice, order.currency)}
          </Text>
        </InlineStack>
      </IndexTable.Cell>
      
      <IndexTable.Cell>
        <Badge {...getStatusBadge(order.financial_status)} />
      </IndexTable.Cell>
      
      <IndexTable.Cell>
        <Tooltip content={t('common.viewDetails')}>
          <Button 
            size="micro" 
            icon={ViewMajor}
            onClick={() => navigateWithContext(`/order/${order.id}`)}
          />
        </Tooltip>
      </IndexTable.Cell>
    </IndexTable.Row>
  ));

  // Bulk actions for recent orders
  const promotedBulkActions = [
    {
      content: t('common.viewDetails'),
      onAction: () => {
        if (selectedResources.length === 1) {
          navigateWithContext(`/order/${selectedResources[0]}`);
        }
      },
      disabled: selectedResources.length !== 1
    },
  ];

  const bulkActions = [
    {
      content: t('dashboard.viewAllOrders'),
      onAction: () => navigateWithContext('/orders'),
    },
  ];

  if (loading) {
    return (
      <Page title={t('dashboard.title')}>
        <Layout>
          <Layout.Section>
            <Card sectioned>
              <div style={{ textAlign: 'center', padding: '2rem' }}>
                <Spinner size="large" />
                <Text variant="bodyMd" as="p" color="subdued">
                  {t('dashboard.loadingDashboard')}
                </Text>
              </div>
            </Card>
          </Layout.Section>
        </Layout>
      </Page>
    );
  }

  return (
    <Page
      title={t('dashboard.title')}
      subtitle={t('dashboard.subtitle')}
    >
      <Layout>
        {/* Subscription Status - Modern Design */}
        {subscription && (
          <Layout.Section>
            <Card padding="400">
              <Box>
                <InlineStack gap="300" align="space-between">
                  <InlineStack gap="300" align="start">
                    <div style={{
                      backgroundColor: '#fff7ed',
                      padding: '12px',
                      borderRadius: '8px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      <Icon source={SettingsMajor} color="warning" />
                    </div>
                    <BlockStack gap="200">
                      <Text variant="headingMd" as="h3">
                        {t('dashboard.currentPlan')}: {subscription.plan.name}
                      </Text>
                      <Text variant="bodyMd" as="p" color="subdued">
                        {subscription.remainingTracks} de {subscription.plan.trackingLimit} {t('dashboard.remainingTracks')}
                      </Text>
                      <Box paddingBlockStart="200">
                        <ProgressBar
                          progress={((subscription.plan.trackingLimit - subscription.remainingTracks) / subscription.plan.trackingLimit) * 100}
                          size="medium"
                          color={subscription.remainingTracks < 5 ? 'critical' : 'primary'}
                        />
                      </Box>
                    </BlockStack>
                  </InlineStack>

                  {subscription.remainingTracks < 5 && (
                    <Button variant="primary" onClick={() => navigateWithContext('/billing')}>
                      {t('common.upgrade')}
                    </Button>
                  )}
                </InlineStack>
              </Box>
            </Card>
          </Layout.Section>
        )}

        {/* Zetfy Integration Banner - Shows for unconnected users */}
        <Layout.Section>
          <ZyzfyIntegrationBanner
            onConnectionSuccess={() => {
              // Reload dashboard data to reflect new integration status
              loadDashboardData();
            }}
          />
        </Layout.Section>

        {/* Stats Cards - Modern Design with Icons */}
        <Layout.Section>
          <InlineGrid columns={{ xs: '1', sm: '2', md: '3' }} gap="400">
            {/* Total Orders */}
            <Card padding="400">
              <Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    backgroundColor: '#e0f5ff', 
                    padding: '8px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={OrdersMajor} color="highlight" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text variant="headingMd" as="h3" fontWeight="semibold">
                      {stats.totalOrders}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('dashboard.totalOrders')}
                    </Text>
                  </div>
                </div>
              </Box>
            </Card>
            
            {/* Active Journeys */}
            <Card padding="400">
              <Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    backgroundColor: '#e3f1df', 
                    padding: '8px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={EmailMajor} color="success" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text variant="headingMd" as="h3" fontWeight="semibold">
                      {stats.activeJourneys}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('dashboard.activeJourneys')}
                    </Text>
                  </div>
                </div>
              </Box>
            </Card>
            
            {/* Tracked Orders */}
            <Card padding="400">
              <Box>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ 
                    backgroundColor: '#f0f9ff', 
                    padding: '8px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={CircleTickMajor} color="primary" />
                  </div>
                  <div style={{ flex: 1 }}>
                    <Text variant="headingMd" as="h3" fontWeight="semibold">
                      {stats.deliveredOrders}
                    </Text>
                    <Text variant="bodySm" as="p" color="subdued">
                      {t('dashboard.trackedOrders')}
                    </Text>
                  </div>
                </div>
              </Box>
            </Card>
          </InlineGrid>
        </Layout.Section>

        {/* Recent Orders */}
        <Layout.Section>
          <Card>
            <Box padding="400">
              <Text variant="headingMd" as="h3">
                {t('dashboard.recentOrders')}
              </Text>
            </Box>
            
            <IndexTable
              resourceName={resourceName}
              itemCount={recentOrders.length}
              selectedItemsCount={
                allResourcesSelected ? 'All' : selectedResources.length
              }
              onSelectionChange={handleSelectionChange}
              promotedBulkActions={promotedBulkActions}
              bulkActions={bulkActions}
              headings={[
                { title: t('dashboard.tableHeaders.order') },
                { title: t('dashboard.tableHeaders.total') },
                { title: t('dashboard.tableHeaders.status') },
                { title: t('dashboard.tableHeaders.action') },
              ]}
              loading={loading}
              emptyState={
                <EmptyState
                  heading={t('dashboard.noOrdersFound')}
                  action={{
                    content: t('dashboard.viewAllOrders'),
                    onAction: () => navigateWithContext('/orders')
                  }}
                  image="https://cdn.shopify.com/s/files/1/0262/4071/2726/files/emptystate-files.png"
                >
                  <Text variant="bodyMd" as="p">
                    {t('dashboard.noOrdersDescription')}
                  </Text>
                </EmptyState>
              }
            >
              {rowMarkup}
            </IndexTable>
          </Card>
        </Layout.Section>

        {/* Quick Actions - Modern Layout */}
        <Layout.Section>
          <InlineGrid columns={{ xs: '1', sm: '2' }} gap="400">
            {/* Manage Orders */}
            <Card padding="400">
              <Box>
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#e0f5ff', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={OrdersMajor} color="highlight" />
                  </div>
                  <BlockStack gap="300">
                    <BlockStack gap="100">
                      <Text variant="headingMd" as="h3">
                        {t('dashboard.manageOrders')}
                      </Text>
                      <Text variant="bodyMd" as="p" color="subdued">
                        {t('dashboard.manageOrdersDescription')}
                      </Text>
                    </BlockStack>
                    <Button 
                      variant="primary" 
                      onClick={() => navigateWithContext('/orders')}
                    >
                      {t('dashboard.viewOrders')}
                    </Button>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
            
            {/* Email Journeys */}
            <Card padding="400">
              <Box>
                <InlineStack gap="300" align="start">
                  <div style={{ 
                    backgroundColor: '#e3f1df', 
                    padding: '12px', 
                    borderRadius: '8px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}>
                    <Icon source={EmailMajor} color="success" />
                  </div>
                  <BlockStack gap="300">
                    <BlockStack gap="100">
                      <Text variant="headingMd" as="h3">
                        {t('dashboard.emailJourneysTitle')}
                      </Text>
                      <Text variant="bodyMd" as="p" color="subdued">
                        {t('dashboard.emailJourneysDescription')}
                      </Text>
                    </BlockStack>
                    <Button 
                      variant="primary" 
                      onClick={() => navigateWithContext('/email-journeys')}
                    >
                      {t('dashboard.viewJourneys')}
                    </Button>
                  </BlockStack>
                </InlineStack>
              </Box>
            </Card>
          </InlineGrid>
        </Layout.Section>
      </Layout>
    </Page>
  );
}

export default Home;